/**
 * AdminPage — `/admin` — operator dashboard.
 *
 * Polls /api/admin/stats every 60s while the tab is visible. Renders KPI
 * cards (users, engagement, Pro, revenue), top-countries lists, recent
 * webhook activity. Gracefully shows a 403 message for non-admins.
 *
 * Bootstrap path: set `ADMIN_EMAILS=vbraju@gmail.com,...` in Railway env to
 * grant yourself admin without writing SQL. To promote others later, flip
 * their User.isAdmin via Postgres directly.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ApiError,
  api,
  type AdminStats,
  type AdminUser,
  type AdminUsersQuery,
} from "@/api/client";
import { useAuthStore } from "@/store/authStore";
import { flag, flagUrl, resolveCountry } from "@/lib/countries";

function formatNum(n: number): string {
  return new Intl.NumberFormat().format(n);
}

function formatCents(cents: number, currency: string = "USD"): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  } catch {
    return `${currency} ${(cents / 100).toFixed(0)}`;
  }
}

function formatPct(p: number, digits: number = 1): string {
  return `${p.toFixed(digits)}%`;
}

function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const diffSec = Math.round((Date.now() - t) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  return `${Math.round(diffSec / 86400)}d ago`;
}

export function AdminPage() {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);

  const [stats, setStats] = useState<AdminStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  // Fetch on mount + every 60s while visible.
  useEffect(() => {
    if (status !== "authed" || !user?.email) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        // A 403 right at the 15-min access-token boundary is usually a
        // transient token-rotation blip, not a real permission problem. Retry
        // once with a fresh access token before surfacing the error.
        const s = await (async () => {
          try {
            return await api.adminStats();
          } catch (err) {
            if (err instanceof ApiError && err.status === 403) {
              await new Promise((r) => setTimeout(r, 500));
              return await api.adminStats();
            }
            throw err;
          }
        })();
        if (cancelled) return;
        setStats(s);
        setError(null);
        setRefreshedAt(new Date());
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.status === 403) setError("forbidden");
          else if (err.status === 401) setError("unauthorized");
          else setError("server_error");
        } else {
          setError("network_error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    const poll = window.setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(poll);
    };
  }, [status, user?.email]);

  // -- Render --------------------------------------------------------------

  if (status === "unknown") {
    return <Shell><p className="opacity-70 text-sm">Loading…</p></Shell>;
  }

  if (status === "anonymous" || !user?.email) {
    return (
      <Shell>
        <h1 className="text-2xl font-bold">Admin</h1>
        <p className="mt-3 text-sm opacity-80">Sign in with an admin account to view the dashboard.</p>
        <Link to="/" className="mt-4 inline-block rounded-full bg-white/10 px-4 py-2 text-sm font-semibold hover:bg-white/20">
          Back to home
        </Link>
      </Shell>
    );
  }

  if (error === "forbidden") {
    return (
      <Shell>
        <h1 className="text-2xl font-bold">Admin</h1>
        <p className="mt-3 text-sm opacity-80">
          You&rsquo;re signed in as <span className="font-semibold">{user.email}</span>, but this account isn&rsquo;t in the admin allowlist.
        </p>
        <p className="mt-2 text-xs opacity-60">
          To grant access: add this email to <code className="rounded bg-white/10 px-1">ADMIN_EMAILS</code> in Railway env,
          or set <code className="rounded bg-white/10 px-1">isAdmin = true</code> for this User row in Postgres.
        </p>
        <Link to="/" className="mt-4 inline-block rounded-full bg-white/10 px-4 py-2 text-sm font-semibold hover:bg-white/20">
          Back to home
        </Link>
      </Shell>
    );
  }

  if (!stats) {
    return (
      <Shell>
        <h1 className="text-2xl font-bold">Admin</h1>
        <p className="mt-3 text-sm opacity-70">
          {loading ? "Loading stats…" : error ? "Couldn't load stats." : "—"}
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-2xl font-bold">Admin · {stats.windowUtc.today}</h1>
        <p className="text-xs opacity-60">
          Refreshed {refreshedAt ? relativeTime(refreshedAt.toISOString()) : "—"} · auto every 60s
        </p>
      </div>

      {/* ─────────── USERS ─────────── */}
      <SectionTitle>Users</SectionTitle>
      <Grid>
        <Kpi label="Total users" value={formatNum(stats.users.total)} sub={`${formatPct(stats.users.signedInPct, 1)} signed in`} />
        <Kpi label="New today" value={formatNum(stats.users.newToday)} />
        <Kpi label="New · 7d" value={formatNum(stats.users.new7d)} />
        <Kpi label="New · 30d" value={formatNum(stats.users.new30d)} />
      </Grid>

      {/* ─────────── ENGAGEMENT ─────────── */}
      <SectionTitle>Engagement</SectionTitle>
      <Grid>
        <Kpi label="DAU" value={formatNum(stats.engagement.dau)} accent="emerald" />
        <Kpi label="WAU" value={formatNum(stats.engagement.wau)} />
        <Kpi label="MAU" value={formatNum(stats.engagement.mau)} />
        <Kpi label="Attempts today" value={formatNum(stats.engagement.attemptsToday)} />
        <Kpi label="Attempts · 7d" value={formatNum(stats.engagement.attempts7d)} />
        <Kpi label="Attempts · 30d" value={formatNum(stats.engagement.attempts30d)} />
        <Kpi label="Attempts · total" value={formatNum(stats.engagement.attemptsTotal)} />
      </Grid>

      {/* ─────────── PRO & REVENUE ─────────── */}
      <SectionTitle>Pro &amp; Revenue</SectionTitle>
      <Grid>
        <Kpi label="Active Pro" value={formatNum(stats.pro.activeCount)} accent="amber" />
        <Kpi label="New Pro today" value={formatNum(stats.pro.newToday)} />
        <Kpi label="New Pro · 7d" value={formatNum(stats.pro.new7d)} />
        <Kpi label="Conversion (Pro / signed-in)" value={formatPct(stats.pro.conversionPct, 2)} />
        <Kpi label="MRR (est.)" value={formatCents(stats.revenue.estMrrCents)} accent="amber" />
        <Kpi label="Revenue · 7d" value={formatCents(stats.revenue.totalCents7d)} />
        <Kpi label="Revenue · 30d" value={formatCents(stats.revenue.totalCents30d)} />
        <Kpi
          label="Refunds · 30d"
          value={`${stats.revenue.refundsCount30d} · ${formatPct(stats.revenue.refundRate30dPct, 1)}`}
          {...(stats.revenue.refundRate30dPct > 5 ? { accent: "rose" as const } : {})}
        />
      </Grid>

      {/* ─────────── COUNTRIES ─────────── */}
      <SectionTitle>Top countries</SectionTitle>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Panel title={`Today — attempts (${stats.windowUtc.today})`}>
          <CountryList rows={stats.countries.topByAttemptsToday} total={stats.engagement.attemptsToday} />
        </Panel>
        <Panel title="All-time — users">
          <CountryList rows={stats.countries.topByUsersTotal} total={stats.users.total} />
        </Panel>
      </div>

      {/* ─────────── SIGNED-UP USERS ─────────── */}
      <SectionTitle>Signed-up users</SectionTitle>
      <UsersPanel />

      {/* ─────────── FRIEND LEAGUES ─────────── */}
      <SectionTitle>Friend Leagues</SectionTitle>
      <Grid>
        <Kpi label="Total leagues" value={formatNum(stats.leagues.total)} />
        <Kpi label="Total memberships" value={formatNum(stats.leagues.members)} />
        <Kpi label="Created today" value={formatNum(stats.leagues.createdToday)} />
      </Grid>

      {/* ─────────── WEBHOOKS ─────────── */}
      <SectionTitle>Recent webhook events</SectionTitle>
      <div className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10">
        {stats.webhooks.recent.length === 0 ? (
          <p className="text-sm opacity-60">No webhook events yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {stats.webhooks.recent.map((w) => (
              <li key={w.id} className="flex items-center justify-between gap-2 text-xs">
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                      w.source === "dodo" ? "bg-amber-300/20 text-amber-200" : "bg-violet-300/20 text-violet-200"
                    }`}
                  >
                    {w.source}
                  </span>
                  <span className="truncate font-mono opacity-80">{w.eventType}</span>
                </div>
                <span className="shrink-0 opacity-60">{relativeTime(w.processedAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="mt-6 text-[11px] opacity-50">
        All numbers are computed live from Postgres. Window boundaries are UTC. Revenue does not include processing fees;
        MRR is an estimate (active annual subs ÷ 12). Open <a href="https://api.curvle.world/health" className="underline">health probe</a> for a quick DB/Redis sanity check.
      </p>
    </Shell>
  );
}

// ─────────── building blocks ───────────

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-emerald-950 text-white">
      {/* Page chrome — just the wordmark on the left. The right side is
          owned by the global UserPill (from RootLayout), which already
          surfaces Account, Friend Leagues, Admin dashboard, and now
          Leaderboard via the dropdown menu — so duplicating those pills
          here would just compete with the global pill on narrow screens. */}
      <header className="border-b border-white/10 px-4 py-3">
        <div className="mx-auto flex max-w-5xl items-center">
          <Link to="/" className="text-sm font-bold tracking-wider opacity-80 hover:opacity-100">
            CURVLE · ADMIN
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-6">{children}</main>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mt-7 mb-2 text-xs font-bold uppercase tracking-wider opacity-60">{children}</h2>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{children}</div>;
}

function Kpi({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: "amber" | "emerald" | "rose";
}) {
  const accentClass =
    accent === "amber"
      ? "ring-amber-300/30"
      : accent === "emerald"
      ? "ring-emerald-300/30"
      : accent === "rose"
      ? "ring-rose-400/40"
      : "ring-white/10";
  const valueClass =
    accent === "amber"
      ? "text-amber-200"
      : accent === "emerald"
      ? "text-emerald-200"
      : accent === "rose"
      ? "text-rose-300"
      : "text-white";
  return (
    <div className={`rounded-2xl bg-white/5 p-3 ring-1 ${accentClass}`}>
      <p className="text-[11px] uppercase tracking-wider opacity-60">{label}</p>
      <p className={`mt-1 text-xl font-bold tabular-nums ${valueClass}`}>{value}</p>
      {sub && <p className="mt-0.5 text-[11px] opacity-50">{sub}</p>}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10">
      <p className="text-[11px] font-semibold uppercase tracking-wider opacity-60">{title}</p>
      <div className="mt-2">{children}</div>
    </div>
  );
}

/**
 * UsersPanel — paginated, filterable list of signed-up users. Backed by
 * GET /api/admin/users. Polls only on user action (search/sort/load-more),
 * not on the 60s auto-refresh — these requests are heavier than /stats and
 * surface email addresses, so we don't want them flying around in the
 * background.
 *
 * The first 100 sorted by createdAt asc is the Founder list — useful on
 * Pro launch day for the SQL UPDATE that flips them to isPro=true.
 */
function UsersPanel() {
  const PAGE_SIZE = 50;
  const [rows, setRows] = useState<AdminUser[] | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const [sort, setSort] = useState<"createdAt" | "lastPlayedAt" | "currentStreak">("createdAt");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [proOnly, setProOnly] = useState(false);

  // Initial + filter-change load. Debounce email search so we don't fire
  // a request per keystroke.
  useEffect(() => {
    let cancelled = false;
    const t = window.setTimeout(async () => {
      const query: AdminUsersQuery = {
        offset: 0,
        limit: PAGE_SIZE,
        sort,
        order,
        ...(q.trim().length >= 2 ? { q: q.trim() } : {}),
        ...(proOnly ? { pro: true } : {}),
      };
      setLoading(true);
      setErr(null);
      try {
        const res = await api.adminUsers(query);
        if (cancelled) return;
        setRows(res.rows);
        setTotal(res.total);
        setHasMore(res.hasMore);
      } catch (e) {
        if (cancelled) return;
        setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : "load_failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [q, sort, order, proOnly]);

  const loadMore = async () => {
    if (loadingMore || !rows || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await api.adminUsers({
        offset: rows.length,
        limit: PAGE_SIZE,
        sort,
        order,
        ...(q.trim().length >= 2 ? { q: q.trim() } : {}),
        ...(proOnly ? { pro: true } : {}),
      });
      setRows((prev) => [...(prev ?? []), ...res.rows]);
      setTotal(res.total);
      setHasMore(res.hasMore);
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : "load_failed");
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10">
      {/* Filter row */}
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
        <input
          type="search"
          inputMode="email"
          placeholder="Search email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="min-w-[14rem] flex-1 rounded-full bg-white/10 px-3 py-1.5 placeholder:opacity-40 focus:bg-white/15 focus:outline-none"
        />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as typeof sort)}
          className="rounded-full bg-white/10 px-3 py-1.5 focus:outline-none"
        >
          <option value="createdAt">Sort: joined</option>
          <option value="lastPlayedAt">Sort: last played</option>
          <option value="currentStreak">Sort: current streak</option>
        </select>
        <select
          value={order}
          onChange={(e) => setOrder(e.target.value as typeof order)}
          className="rounded-full bg-white/10 px-3 py-1.5 focus:outline-none"
        >
          <option value="desc">Newest first</option>
          <option value="asc">Oldest first</option>
        </select>
        <label className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5">
          <input
            type="checkbox"
            checked={proOnly}
            onChange={(e) => setProOnly(e.target.checked)}
            className="h-3 w-3 accent-amber-300"
          />
          <span>Pro only</span>
        </label>
        <span className="ml-auto opacity-60">
          {loading ? "Loading…" : `${formatNum(total)} total`}
        </span>
      </div>

      {err && <p className="mb-2 text-xs text-rose-300">Could not load users: {err}</p>}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-xs">
          <thead className="text-[10px] uppercase tracking-wider opacity-50">
            <tr>
              <th className="py-1.5 pr-3">Email</th>
              <th className="pr-3">Display name</th>
              <th className="pr-3">Country</th>
              <th className="pr-3 text-center">Pro</th>
              <th className="pr-3 text-right">Streak</th>
              <th className="pr-3">Joined</th>
              <th>Last played</th>
            </tr>
          </thead>
          <tbody>
            {rows && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="py-4 text-center opacity-60">
                  No users match those filters.
                </td>
              </tr>
            )}
            {rows && rows.map((u) => (
              <tr key={u.id} className="border-t border-white/5">
                <td className="py-1.5 pr-3 font-mono">{u.email ?? <span className="opacity-40">— (anonymous)</span>}</td>
                <td className="pr-3 truncate">{u.displayName ?? <span className="opacity-40">—</span>}</td>
                <td className="pr-3">
                  <span className="inline-flex items-center gap-1.5">
                    {flagUrl(u.countryCode, 40) ? (
                      <img
                        src={flagUrl(u.countryCode, 40)!}
                        width={16}
                        height={12}
                        alt=""
                        className="rounded-[2px] shadow-[0_0_0_1px_rgba(255,255,255,0.15)]"
                      />
                    ) : (
                      <span aria-hidden="true">{flag(u.countryCode)}</span>
                    )}
                    <span className="opacity-70">{u.countryCode}</span>
                  </span>
                </td>
                <td className="pr-3 text-center">
                  {u.isPro ? (
                    <span className="rounded-full bg-amber-300/90 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-950">
                      Pro
                    </span>
                  ) : (
                    <span className="opacity-30">—</span>
                  )}
                  {u.isAdmin && (
                    <span className="ml-1 rounded-full bg-violet-300/90 px-1.5 py-0.5 text-[10px] font-bold uppercase text-violet-950">
                      Admin
                    </span>
                  )}
                </td>
                <td className="pr-3 text-right tabular-nums">
                  {u.currentStreak}
                  {u.longestStreak > u.currentStreak && (
                    <span className="opacity-40"> · max {u.longestStreak}</span>
                  )}
                </td>
                <td className="pr-3 opacity-80" title={u.createdAt}>{relativeTime(u.createdAt)}</td>
                <td className="opacity-80" title={u.lastPlayedAt ?? ""}>
                  {u.lastPlayedAt ? relativeTime(u.lastPlayedAt) : <span className="opacity-40">never</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <button
          type="button"
          disabled={loadingMore}
          onClick={loadMore}
          className="mx-auto mt-3 block rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold transition hover:bg-white/20 disabled:opacity-50"
        >
          {loadingMore ? "Loading…" : `Show next ${PAGE_SIZE}`}
        </button>
      )}
    </div>
  );
}

function CountryList({
  rows,
  total,
}: {
  rows: Array<{ code: string; count: number }>;
  total: number;
}) {
  if (rows.length === 0) {
    return <p className="text-sm opacity-60">No data yet.</p>;
  }
  return (
    <ul className="space-y-1">
      {rows.map((r) => {
        const pct = total > 0 ? (r.count / total) * 100 : 0;
        return (
          <li key={r.code} className="flex items-center gap-3 text-sm">
            <span aria-hidden="true">{flag(r.code)}</span>
            <span className="min-w-0 flex-1 truncate">{resolveCountry(r.code).name}</span>
            <span className="w-20 text-right font-semibold tabular-nums">
              {formatNum(r.count)}
            </span>
            <span className="w-12 text-right text-xs opacity-60 tabular-nums">
              {formatPct(pct, 0)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
